import { useState, useRef, useEffect } from "react";
import { Box, Paper, Typography, ToggleButton, ToggleButtonGroup, Skeleton, Stack } from "@mui/material";
import { BarChart } from "@mui/x-charts";

const TOGGLE_SX = (fontSize = 11) => ({
  "& .MuiToggleButton-root": {
    color: "#b0b0b0",
    borderColor: "rgba(255,255,255,0.2)",
    fontSize,
    py: 0.25,
    px: 1,
    textTransform: "none",
  },
  "& .Mui-selected": {
    backgroundColor: "rgba(69, 144, 149, 0.35)",
    color: "#eaeaea",
    "&:hover": { backgroundColor: "rgba(69, 144, 149, 0.5)" },
  },
});

const DnsRequestsPanel = ({ dnsCountData, dnsCountLoading, products, paper_sx }) => {
  const [dnsChartView, setDnsChartView] = useState("hour");
  const chartContainerRef = useRef(null);
  const [chartHeight, setChartHeight] = useState(240);

  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect?.height;
      if (h > 0) setChartHeight(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const hasHour = (dnsCountData.byHour || []).some((d) => (d?.value ?? 0) > 0);
  const hasRrtype = (dnsCountData.byRrtype || []).length > 0;
  const hasRrname = (dnsCountData.byRrname || []).length > 0;
  const anyData = hasHour || hasRrtype || hasRrname;

  const renderChart = () => {
    if (!anyData) return (
      <Typography variant="body2" sx={{ color: "#777", py: 4, textAlign: "center" }}>
        No DNS requests in the last 24h (filter: app_proto = dns).
      </Typography>
    );

    if (dnsChartView === "hour" && hasHour) {
      const byHour = dnsCountData.byHour || [];
      return (
        <BarChart
          height={chartHeight}
          series={[{ data: byHour.map((d) => d?.value ?? 0), color: "#459296", label: "DNS" }]}
          xAxis={[{ scaleType: "band", data: byHour.map((d) => d?.label), tickLabelStyle: { fontSize: 11 } }]}
          yAxis={[{ label: "Count", tickLabelStyle: { fontSize: 11 } }]}
          margin={{ top: 10, right: 20, bottom: 30, left: 0 }}
          slotProps={{ legend: { style: { display: "none" } } }}
        />
      );
    }

    if (dnsChartView === "rrtype" && hasRrtype) {
      const list = (dnsCountData.byRrtype || []).slice(0, 12);
      return (
        <BarChart
          height={chartHeight}
          layout="horizontal"
          series={[{ data: list.map((d) => d?.value ?? 0), color: "#459296" }]}
          yAxis={[{ scaleType: "band", data: list.map((d) => d?.label != null ? String(d.label) : ""), tickLabelStyle: { fontSize: 12 } }]}
          xAxis={[{ tickLabelStyle: { fontSize: 11 } }]}
          margin={{ top: 10, right: 20, bottom: 20, left: 0 }}
          slotProps={{ legend: { style: { display: "none" } } }}
        />
      );
    }

    if (dnsChartView === "rrname" && hasRrname) {
      const list = (dnsCountData.byRrname || []).slice(0, 12);
      return (
        <BarChart
          height={chartHeight}
          layout="horizontal"
          series={[{ data: list.map((d) => d?.value ?? 0), color: "#459296" }]}
          yAxis={[{ scaleType: "band", data: list.map((d) => d?.label != null ? String(d.label) : ""), tickLabelStyle: { fontSize: 12 }, width: 150 }]}
          xAxis={[{ tickLabelStyle: { fontSize: 11 } }]}
          margin={{ top: 10, right: 20, bottom: 20, left: 0 }}
          slotProps={{ legend: { style: { display: "none" } } }}
        />
      );
    }

    return (
      <Typography variant="body2" sx={{ color: "#777", py: 4, textAlign: "center" }}>
        No data for this view.
      </Typography>
    );
  };

  return (
    <Paper sx={{...paper_sx, maxHeight: 420, display: "flex", flexDirection: "column", }} elevation={3}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#888" }}>
          DNS requests (last 24h)
        </Typography>
        <ToggleButtonGroup
          value={dnsChartView}
          exclusive
          onChange={(_, v) => v != null && setDnsChartView(v)}
          size="small"
          sx={TOGGLE_SX(10)}
        >
          <ToggleButton value="hour">By hour</ToggleButton>
          {products?.lecs && !products.agent && <ToggleButton value="rrtype">By type</ToggleButton>}
          <ToggleButton value="rrname">By domain</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box ref={chartContainerRef} sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
        {dnsCountLoading ? (
          <Stack direction="row" spacing={0.75} alignItems="flex-end" sx={{ height: "100%", px: 1, pb: 1 }}>
            {[40, 65, 30, 80, 50, 70, 35, 90, 45, 60, 25, 75].map((h, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                sx={{ flex: 1, height: `${h}%`, bgcolor: "rgba(255,255,255,0.07)", borderRadius: "4px 4px 0 0" }}
              />
            ))}
          </Stack>
        ) : renderChart()}
      </Box>
    </Paper>
  );
};

export default DnsRequestsPanel;
